import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext/AuthContext";
import { logoutUrl, networkError } from "../utils/constants";
import { XSquare } from "react-bootstrap-icons";
import Cookies from 'js-cookie';

const errorEmitter = {
  listeners: [],
  emit: function(status) {
    this.listeners.forEach(cb => cb(status));
  },
  subscribe: function(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }
};

const useApis = (initialUrl, initialMethod = "GET") => {
  const [serverError, setServerError] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resultGet, setResultGet] = useState(null);
  const [resultPost, setResultPost] = useState(null);
  const [url, setUrl] = useState(initialUrl);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [message, setMessage] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showTemporarilyDown, setShowTemporarilyDown] = useState(false);

  const refreshFailed = false;
  const isRefreshing = useRef(false); // Tracks if refresh is in progress
  const pendingRequests = useRef([]); // Tracks pending requests

  const navigate = useNavigate();
  const auth = useAuth();
  const [method, setMethod] = useState(initialMethod);


  useEffect(() => {
    const unsubscribe = errorEmitter.subscribe((status) => {
      if (status === 500 || status === 504) {
        setShowTemporarilyDown(true);
      }
    });

    return unsubscribe;
  }, []);

  const isTokenError = (errorMessage) => {
    return (
      errorMessage === "Token is missing" ||
      errorMessage === "Token has expired"
    );
  };

  const showTokenExpiredToaster = () => {
    setTokenError("Session expired. Please Login again!");
    auth?.showToast(
      "Error",
      "Session expired. Please Login again!",
      "../assets/mhc.png",
      <XSquare />
    );
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setServerError("");
    }, 3000);
    auth?.logout();
  };

  // Listen for online/offline events
  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);

    globalThis.addEventListener("online", updateOnlineStatus);
    globalThis.addEventListener("offline", updateOnlineStatus);

    return () => {
      globalThis.removeEventListener("online", updateOnlineStatus);
      globalThis.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  const callApi = async (
    data = null,
    customUrl = null,
    customMethod = null,
    token = true,
    headers = token
      ? {
        Authorization: `Bearer ${Cookies.get("access_token")}`,
        "Content-Type": "application/json",
      }
      : {}
  ) => {
    if (!isOnline) {
      setServerError(networkError);
      setShowError(true);
      return;
    }

    setServerError("");
    setIsLoading(true);

    const requestUrl = customUrl || url;
    const requestMethod = customMethod || method;

    const filterUndefinedValues = (obj) => {
      return Object.fromEntries(
        Object.entries(obj).filter(([_, value]) => value !== undefined)
      );
    };

    const sanitizedData = data ? filterUndefinedValues(data) : null;

    const config = {
      url: requestUrl,
      method: requestMethod,
      data: sanitizedData,
      headers: {
        ...headers,
        Authorization: `Bearer ${Cookies.get("access_token")}`,
      },
    };

    try {
      const response = await axios(config);
      handleApiResponse(response, requestMethod);
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApiResponse = (response, requestMethod) => {
    if (requestMethod !== "GET") {
      if (response.data) {
        setResultPost(response.data);
        setShowSuccess(true);
        setMessage({ postMessage: response.message });
        setModalShow(true);
      } else {
        setMessage("No data provided for the request.");
      }
    } else {
      setResultGet(response.data);
      setShowSuccess(true);
      if (response.data.message) {
        setMessage({ getMessage: response.message });
      }
    }

  };

  const handleApiError = (error) => {
    setShowError(true);
    const rawFallback = error?.response?.data;
    const isUnsafeRawBody =
      typeof rawFallback !== "string" || rawFallback.includes("doctype html");
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.response?.data?.detail ||
      (error?.message === "Network Error"
        ? networkError
        : isUnsafeRawBody
          ? "Your request has failed!"
          : rawFallback);

    if (isTokenError(errorMessage)) {
      showTokenExpiredToaster();
    } else {
      setServerError(errorMessage);
    }

    handleTokenExpiration(error);
  };

  const handleTokenExpiration = (error) => {

  if (error?.response?.status === 404 && typeof error?.response?.data === 'string' && error.response.data.includes('<!DOCTYPE html>')) {
    setServerError("Requested page is not found");
    return;
  }

  if (error?.response?.status === 503) {
      setServerError("This staging environment is unavailable daily between 8:00 PM and 6:00 AM UTC. Please check back after 6:00 AM UTC.");
      return;
  }

    if (error?.response?.status === 500 || error?.response?.status === 504) {
      setIsLoading(true);
      errorEmitter.emit(error?.response?.status);
      setIsLoading(false);
      return;
    }

  if (typeof error?.response?.data === 'string' && error.response.data.includes('<!DOCTYPE html>')) {
    return setServerError("An unknown error has occurred");
  }


    if (
      error.response?.status === 401 &&
      error.response?.data?.token_expired
    ) {
      if (!refreshFailed) {
        pendingRequests.current.push(() => callApi());
        if (!isRefreshing.current) {
          isRefreshing.current = true; // Set to true while refreshing
          callApi(); // Retry the API call after refreshing the token
        }
      }
    } else if (error.response?.data?.unauthorized) {
      setTimeout(() => {
        auth.logout();
        callApi(null, logoutUrl, "POST");
        localStorage.clear()
        navigate("/login");
        auth?.showToast(
          "Error",
          "You have logged in to another device",
          "../assets/mhc.png",
          <XSquare />
        );
      }, 3000);
    } else if (error.response?.status === 401 && error.response?.data?.session_expired) {
      setServerError("Session has expired. Please log in again.");
      setTimeout(() => {
        auth.logout();
        callApi(null, logoutUrl, "POST");
        localStorage.clear()
        navigate("/login");
      }, 3000);
    }
  };

  return {
    callApi,
    setUrl,
    setMethod,
    serverError,
    tokenError,
    isLoading,
    resultGet,
    resultPost,
    showSuccess,
    showError,
    modalShow,
    message,
    setModalShow,
    setShowSuccess,
    setShowError,
    isOnline, // Expose online status
    toggleShowSuccess: () => setShowSuccess((prev) => !prev),
    toggleShowError: () => setShowError((prev) => !prev),
    showTemporarilyDown,
    setShowTemporarilyDown,
  };
};

export default useApis;